import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReviewPeriodStatusEnum } from 'src/app/enums/review-period-status.enum';
import { ReviewTypeEnum } from 'src/app/enums/review-type.enum';
import { AssessmentHelper } from 'src/app/helpers/assessment.helper';
import { ReviewPeriodHelper } from 'src/app/helpers/review-period.helper';
import { IdTranslateKey } from 'src/app/models/general/id-translate-key.model';
import { IResult, Result } from 'src/app/models/general/result.model';
import { AssessmentActions } from 'src/app/models/other/assessment-actions.model';
import { QuestionModule } from 'src/app/models/question-module.model';
import { ReviewPeriodCustomGoal } from 'src/app/models/review-period-custom-goal.model';
import { ReviewPeriodPositionQuestionSet } from 'src/app/models/review-period-position-questionset.model';
import { ReviewPeriodQuestionSettings, ReviewPeriodQuestionSettingsModel } from 'src/app/models/review-period-question-settings.model';
import { ReviewPeriodStatus } from 'src/app/models/review-period-status.model';
import { ReviewPeriod, ReviewPeriodModel } from 'src/app/models/review-period.model';
import { ReviewType } from 'src/app/models/review-type.model';
import { ReviewPeriodService } from 'src/app/services';
import { CustomValidators } from 'src/app/shared/helpers/custom-validators';
import { ErrorMessageService } from 'src/app/shared/services';
import { SettingsState } from 'src/app/store/settings.state';

@UntilDestroy()
@Component({
  selector: 'app-review-period-edit',
  templateUrl: './review-period-edit.component.html',
  styleUrls: ['./review-period-edit.component.scss']
})
export class ReviewPeriodEditComponent implements OnInit {  

  @Output()
  public closeEdit = new EventEmitter();

  @Output()
  public saved = new EventEmitter<number>();

  public reviewTypes: IdTranslateKey[];
  public readonly reviewTypesEnum = ReviewTypeEnum;

  @Input()
  public selectedReviewPeriod: ReviewPeriod;

  public prevReviewPeriodList: ReviewPeriod[] = [];

  public questionSetList: ReviewPeriodPositionQuestionSet[] = [];
  public assessmentActions: AssessmentActions;

  public reviewPeriodCustomGoals: ReviewPeriodCustomGoal[] = [];

  // Kérdőív beállítások
  public get questionSettingsArray(): FormArray {
    return this.editForm.get('questionSettings') as FormArray;
  } 

  public isDebug = false;
  
  public editForm: UntypedFormGroup = null;

  public addMode = false;
  private isReviewExists = true;

  public questionModules: QuestionModule[];

  public statusList: IdTranslateKey[];

  public get allowSettingsEdit(): boolean {
    if (this.addMode) return true;

    return !this.isReviewExists;
  }

  public get isLatestReviewPeriodSelected(): boolean {
    if (!this.selectedReviewPeriod || !this.settingsState.reviewPeriodList.value) return false;
    if (this.selectedReviewPeriod.reviewType != ReviewTypeEnum.TER) return true;

    const lastInState = ReviewPeriodHelper.getLastTER(this.settingsState.reviewPeriodList.value);
    return lastInState.id === this.settingsState.reviewPeriod.value.id;
  }

  public get selectedReviewType(): ReviewTypeEnum {
    return this.editForm.get('reviewType')?.value;
  }

  public get isPositionBasedAssessment(): boolean {
    return this.editForm.controls['isPositionBasedAssessment']?.value;
  }

  public get isAura(): boolean {
    return this.editForm.controls['isSpecialQuestionSetBasedAssessment']?.value;
  }

  public get isStressMap(): boolean {
    return this.selectedReviewPeriod?.reviewType === ReviewTypeEnum.MilioStressMap;
  }

  public get isGoalsEnabled(): boolean {
    return this.editForm.controls['isGoalsEnabled']?.value;
  }

  public get selectedQuestionSets(): ReviewPeriodPositionQuestionSet[] {
    let resultList: ReviewPeriodPositionQuestionSet[] = [];

    if (this.isPositionBasedAssessment) {
      const selected = this.questionSetList.filter(x => x.isPositionBasedAssessment);
      resultList.push(...selected);
    }

    if (this.isAura) {
      const selected = this.questionSetList.filter(x => x.isSpecialQuestionSetBasedAssessment);
      resultList.push(...selected);
    }

    return resultList;
  }

  public get displaySelectedQuestionSets(): ReviewPeriodPositionQuestionSet[] {
    const toDisplay = this.selectedQuestionSets.filter(x => !x.isSpecialQuestionSetBasedAssessment);
    return toDisplay;
  }
  
  public isPanorama = ReviewPeriodHelper.isPanoramaAuraGoals;
  public isOnlyGoals = ReviewPeriodHelper.isOnlyGoals;

  constructor(
    private fb: UntypedFormBuilder,
    private reviewPeriodService: ReviewPeriodService,
    private msgService: ErrorMessageService,
    private settingsState: SettingsState,
    private cd: ChangeDetectorRef,
    private translate: TranslateService
    ) { }

  ngOnInit(): void {
    this.statusList = this.reviewPeriodService.getReviewPeriodStatusList();
    this.reviewTypes = this.reviewPeriodService.getReviewTypes();

    this.editForm = this.createEditForm();
    
    this.addPanoramaAuraDateValidation(this.editForm);
    this.addGoalSettingsValidation(this.editForm);
  }

  private addPanoramaAuraDateValidation(form: UntypedFormGroup): void {
    const isPositionBasedAssessmentControl = form.get('isPositionBasedAssessment');
    const isSpecialQuestionSetBasedAssessmentControl = form.get('isSpecialQuestionSetBasedAssessment');
    const isGoalsEnabledControl = form.get('isGoalsEnabled');
    const panoramaAuraStartDateControl = form.get('panoramaAuraStartDate');
  
    // Figyeljük, ha a két checkbox bármelyike megváltozik
    const updateValidators = () => {
      const isPositionBased = isPositionBasedAssessmentControl?.value;
      const isSpecialQuestionSet = isSpecialQuestionSetBasedAssessmentControl?.value;
  
      if (!isPositionBased && !isSpecialQuestionSet)
      {
        form.get('panoramaAuraStartDate').setValue(null);
        form.get('panoramaAuraEndDate').setValue(null);
      }

      if (!isGoalsEnabledControl)
      {
        form.get('goalsStartDate').setValue(null);
        form.get('goalsEndDate').setValue(null);
      }

      if ((isPositionBased || isSpecialQuestionSet) && !panoramaAuraStartDateControl.hasValidator(Validators.required)) {
        panoramaAuraStartDateControl?.setValidators([Validators.required]);
      }
      
      if (!isPositionBased && !isSpecialQuestionSet && panoramaAuraStartDateControl.hasValidator(Validators.required))
      {
        panoramaAuraStartDateControl?.clearValidators();
      }
  
      // Fontos: érvényesítjük a változást
      panoramaAuraStartDateControl?.updateValueAndValidity();
    };
  
    // Mindkét mező változását figyeljük
    isPositionBasedAssessmentControl?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(updateValidators);

    isSpecialQuestionSetBasedAssessmentControl?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(updateValidators);

    isGoalsEnabledControl?.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(val => {
      this.cd.detectChanges();
    });
  }

  private addGoalSettingsValidation(form: UntypedFormGroup): void {
    const settingsGroup = form.get('reviewPeriodGoalSettings'); // as UntypedFormGroup;
    if (!settingsGroup) return;

    const isAllowChangeControl = settingsGroup.get('isAllowChange');

    const isTextBasedGoalsEnabledControl = settingsGroup.get('isTextBasedGoalsEnabled');
    const isNumericGoalsEnabledControl = settingsGroup.get('isNumericGoalsEnabled');
    const isGroupGoalsEnabledControl = settingsGroup.get('isGroupGoalsEnabled');
    //const isFirstStateDisabledControl = settingsGroup.get('firstStateDisabled');

    const textControls = [
      'textBasedGoalsBlockNameHu',
      'textBasedGoalsBlockNameEn',
      'textBasedGoalsDefaultWeight',
      'sliderMin',
      'sliderMax',
      'sliderStep'
    ];

    const numericControls = [
      'numericGoalsBlockNameHu',
      'numericGoalsBlockNameEn',
      'numericGoalsDefaultWeight',
      'numericGoalMaxResult'
    ];

    const updateControls = () => {
      const isAllowed = isAllowChangeControl?.value === true;

      // Master toggles
      if (isAllowed) {
        isTextBasedGoalsEnabledControl?.enable({ emitEvent: false });
        isNumericGoalsEnabledControl?.enable({ emitEvent: false });
        isGroupGoalsEnabledControl?.enable({ emitEvent: false });
        //isFirstStateDisabledControl?.enable({ emitEvent: false });
      } else {
        isTextBasedGoalsEnabledControl?.disable({ emitEvent: false });
        isNumericGoalsEnabledControl?.disable({ emitEvent: false });
        isGroupGoalsEnabledControl?.disable({ emitEvent: false });
        //isFirstStateDisabledControl?.disable({ emitEvent: false });
      }

      // Text based sub-controls
      const isTextEnabled = isAllowed && isTextBasedGoalsEnabledControl?.value === true;
      textControls.forEach(key => {
        const control = settingsGroup.get(key);
        if (isTextEnabled) {
          control?.enable({ emitEvent: false });
        } else {
          control?.disable({ emitEvent: false });
        }
      });

      // Numeric sub-controls
      const isNumericEnabled = isAllowed && isNumericGoalsEnabledControl?.value === true;
      numericControls.forEach(key => {
        const control = settingsGroup.get(key);
        if (isNumericEnabled) {
          control?.enable({ emitEvent: false });
        } else {
          control?.disable({ emitEvent: false });
        }
      });
    };

    // Initial state
    updateControls();

    // Listen to changes
    isAllowChangeControl?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => updateControls());
    isTextBasedGoalsEnabledControl?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => updateControls());
    isNumericGoalsEnabledControl?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => updateControls());
  }

  public loadData(reviewPeriod: ReviewPeriod) {    
    this.addMode = !reviewPeriod.id;
            
    this.reviewPeriodService.getForEdit(reviewPeriod.id)
    .pipe(untilDestroyed(this))
    .subscribe(data => {
      const emptyRow = new ReviewPeriodModel();
      emptyRow.id = null;
      emptyRow.name = this.translate.instant('REVIEW_PERIOD_EDIT.CHOOSE');

      this.prevReviewPeriodList = [emptyRow, ...data.reviewPeriodsToContinue.filter(x => x.id != reviewPeriod.id)];

      this.reviewPeriodCustomGoals = data.reviewPeriodCustomGoals || [];

      if (this.addMode) // New
      {
        this.assessmentActions = AssessmentHelper.getAssessmentActions(reviewPeriod.reviewType);
        this.editForm.patchValue(reviewPeriod);
      }           
      else  // Edit
      {
        this.assessmentActions = AssessmentHelper.getAssessmentActions(data.reviewPeriod.reviewType);
        this.editForm.patchValue(data.reviewPeriod);
      }

      this.questionModules = data.questionModules;

      // kérdőívek lista, az összes (Panoráma, aura)
      this.questionSetList = data.questionSets.sort((a,b) => a.questionSetName.localeCompare(b.questionSetName));

      // kérdőív modulok
      this.patchModuleSettings(data.questionSettings ?? []);

      // Call updateModules to populate the list when editing
      if (this.isPositionBasedAssessment || this.isAura) {
        this.updateModules(null);
      }

      this.setControlState();
  
      if (data.reviewPeriod && data.reviewPeriod.id) {
        this.isReviewExists = data.isReviewExist;
      }

      this.cd.detectChanges();      
    });

  }

  public updateModules($event: any)
  {
    let modules: QuestionModule[] = [];

    if (this.isPositionBasedAssessment) {
      const selected = this.questionModules.filter(x => x.isPositionBasedAssessment);
      modules.push(...selected);
    }

    if (this.isAura) {
      const selected = this.questionModules.filter(x => x.isSpecialQuestionSetBasedAssessment);
      modules.push(...selected);
    }

    // add missing modules
    modules.forEach(element => {
      
      const settingsItem = this.questionSettingsArray.controls.find(x => x.get('questionId').value == element.id);

      if (!settingsItem)
      {
        const settings = new ReviewPeriodQuestionSettingsModel();
        settings.questionName = element.name;
        settings.questionId = element.id;   //module id azaz egy question id
        settings.isSpecialQuestion = element.isSpecialQuestionSetBasedAssessment;
    
        this.questionSettingsArray.push(this.createQuestionSettingsGroup(settings));
      }

    });

    let toDelete: number[] = [];

    // remove not used modules
    this.questionSettingsArray.controls.forEach(element => { 
      const item = element.value as ReviewPeriodQuestionSettings;
      const module = modules.find(x => x.id == item.questionId);

      if (!module) 
      {
        toDelete.push(item.questionId);
      }
    });

    toDelete.forEach(questionId => {
      const index = this.questionSettingsArray.controls.findIndex(x => x.get('questionId').value == questionId);
      this.questionSettingsArray.removeAt(index);
    });

  }

  public getQuestionSetName(item: ReviewPeriodPositionQuestionSet): string {
    return item?.questionSetName;
  }

  public getQuestionSetDesc(item: ReviewPeriodPositionQuestionSet): string {
    return item?.questionSetDescription;
  }

  public reviewTypeChanged() {
    this.resetTypeOptions();
    if (this.selectedReviewType === ReviewTypeEnum.PersonalityTest) {
      this.editForm.get('reviewPeriodPositionProfileSettings').setValue({
        reviewPeriodId: 0,
        normalFactorWeight: 1,
        additionalFactorWeight: 0.5,
        keyFactorWeight: 2
      });
    }
    
    this.assessmentActions = AssessmentHelper.getAssessmentActions(this.editForm.get('reviewType').value);
    this.cd.detectChanges();
  }

  private resetTypeOptions() {
    this.editForm.controls['isPositionBasedAssessment'].setValue(false);
    this.editForm.controls['isSpecialQuestionSetBasedAssessment'].setValue(false);
    this.editForm.controls['isGoalsEnabled'].setValue(false);

    this.editForm.get('reviewPeriodPositionProfileSettings').reset();

    this.editForm.controls['panoramaAuraStartDate'].setValue(null);
    this.editForm.controls['panoramaAuraEndDate'].setValue(null);
    this.editForm.controls['goalsStartDate'].setValue(null);
    this.editForm.controls['goalsEndDate'].setValue(null);

    this.editForm.controls['isFreeTextAssessmentAllowed'].setValue(false);
    this.editForm.controls['copyEmployees'].setValue(false);

    const isTER = this.editForm.get('reviewType').value === ReviewTypeEnum.TER;
    this.editForm.controls['isAutomaticReminderEnabled'].setValue(!isTER);
  }
  
  private patchModuleSettings(settings: ReviewPeriodQuestionSettings[]) {
    const settingsArray = <FormArray>this.editForm.get('questionSettings');
    settingsArray.clear();

    settings.forEach(item => {
      settingsArray.push(this.createQuestionSettingsGroup(item));
    });
  }

  private setControlState() {
    const reviewControl = this.editForm.get('reviewType');
    if (this.addMode)
      reviewControl.enable();
    else
      reviewControl.disable();

    const prevReviewPeriodControl = this.editForm.get('previousId');
    if (this.addMode)
      prevReviewPeriodControl.enable();
    else
      prevReviewPeriodControl.disable();
  }

  public saveAdd() {
    if (this.editForm.invalid) return;

    const model = this.editForm.value as ReviewPeriod;
    model.reviewPeriodCustomGoals = this.reviewPeriodCustomGoals;

    model.reviewPeriodGoalSettings.numericGoalMaxResult = model.reviewPeriodGoalSettings.numericGoalMaxResult ? +model.reviewPeriodGoalSettings.numericGoalMaxResult : 0;

    if (model.isGoalsEnabled) {
      model.reviewPeriodGoalSettings.sliderMax = model.reviewPeriodGoalSettings.sliderMax ? +model.reviewPeriodGoalSettings.sliderMax : 0;
      model.reviewPeriodGoalSettings.sliderMin = model.reviewPeriodGoalSettings.sliderMin ? +model.reviewPeriodGoalSettings.sliderMin : 0;
      model.reviewPeriodGoalSettings.sliderStep = model.reviewPeriodGoalSettings.sliderStep ? +model.reviewPeriodGoalSettings.sliderStep : 0;
      model.reviewPeriodGoalSettings.numericGoalMaxResult = model.reviewPeriodGoalSettings.numericGoalMaxResult ? +model.reviewPeriodGoalSettings.numericGoalMaxResult : 0;
    } else {
      model.reviewPeriodGoalSettings = null;
    }

    if (model.reviewType !== ReviewTypeEnum.PersonalityTest) {
      model.reviewPeriodPositionProfileSettings = null;
    }

    this.reviewPeriodService.add(model)
    .pipe(untilDestroyed(this))
    .subscribe({ 
      next: (result) => {
        this.msgService.showSnackBarMessage('reviewPeriodCreated');
        this.settingsState.reviewPeriod.value = result.data;
        this.saved.emit(result.data.id);
        this.closeEditMode();
      }, 
      error: (err: IResult<ReviewPeriod, Result<ReviewPeriod>>) => {
        this.msgService.handleErrorWithData<ReviewPeriod, Result<ReviewPeriod>>(err);
     }}
    );
  }

  public saveEdit() {
    let model = {...this.selectedReviewPeriod};
    model = {...model, ...this.editForm.getRawValue()};
    model.reviewPeriodCustomGoals = this.reviewPeriodCustomGoals;

    if (model.isGoalsEnabled) {
      model.reviewPeriodGoalSettings.sliderMax = model.reviewPeriodGoalSettings.sliderMax ? +model.reviewPeriodGoalSettings.sliderMax : 0;
      model.reviewPeriodGoalSettings.sliderMin = model.reviewPeriodGoalSettings.sliderMin ? +model.reviewPeriodGoalSettings.sliderMin : 0;
      model.reviewPeriodGoalSettings.sliderStep = model.reviewPeriodGoalSettings.sliderStep ? +model.reviewPeriodGoalSettings.sliderStep : 0;
      model.reviewPeriodGoalSettings.numericGoalMaxResult = model.reviewPeriodGoalSettings.numericGoalMaxResult ? +model.reviewPeriodGoalSettings.numericGoalMaxResult : 0;
    } else {
      model.reviewPeriodGoalSettings = null;
    }

    if (model.reviewType !== ReviewTypeEnum.PersonalityTest) {
      model.reviewPeriodPositionProfileSettings = null;
    }
    
    this.reviewPeriodService.update(model)
    .pipe(untilDestroyed(this))
    .subscribe({ 
      next: (result) => {
        this.settingsState.reviewPeriod.value = model;
        this.msgService.showSnackBarMessage('saveSuccessful');
        this.saved.emit(model.id);
        this.closeEditMode();
      }, 
      error: (err) => {
        this.msgService.showErrorResponse(err);
    }});
  }

  public save() {
    if (this.addMode)
      this.saveAdd();
    else
      this.saveEdit();
  }

  public closeEditMode() {
    this.closeEdit.emit();
  }

  public cancel() {
    this.closeEditMode();
  }
    
  private createEditForm(): UntypedFormGroup {
    return this.fb.group({
      'id': 0,
      'reviewType': [{value: 1}, [Validators.required]],
      'startDate': null,
      'endDate' : null,
      'nameHu': ['', [Validators.required]],
      'nameEn': ['', [Validators.required]],
      'questionSettings': this.fb.array([]), 
      'isFreeTextAssessmentAllowed': false,
      'isAutomaticReminderEnabled': true,

      'isPositionBasedAssessment': false,
      'isGoalsEnabled': false,
      'isSpecialQuestionSetBasedAssessment': false,
      
      'panoramaAuraStartDate': null,
      'panoramaAuraEndDate': [null, [
                                      CustomValidators.conditionalRequiredValidator((form) => form.get('isPositionBasedAssessment')?.value === true),
                                      CustomValidators.dateIntervalShouldBeInIntervalValidator('startDate', 'endDate', 'panoramaAuraStartDate')
                                    ]],
      'goalsStartDate': [null],
      'goalsEndDate': [null, [
                              CustomValidators.conditionalRequiredValidator((form) => form.get('isGoalsEnabled')?.value === true),
                              CustomValidators.dateIntervalShouldBeInIntervalValidator('startDate', 'endDate', 'goalsStartDate')
                            ]],
      
      'updateRpe': false,
      
      'copyEmployees': false,
      'status': ReviewPeriodStatusEnum.New,
      'previousId': null,
      'reviewPeriodPositionProfileSettings': this.fb.group({
        'reviewPeriodId': 0,
        'normalFactorWeight': [1, [Validators.required, Validators.min(0)]],
        'additionalFactorWeight': [0.5, [Validators.required, Validators.min(0)]],
        'keyFactorWeight': [2, [Validators.required, Validators.min(0)]]
      }),
      'reviewPeriodGoalSettings': this.fb.group({
        'id': 0,
        'reviewPeriodId': 0,

        'isAllowSettingsForLeader': true,
        'isAllowSettingsForSelf': false,
        'isAllowSettingsForAssistant': true,

        'isAllowChangeDeadlineForLeader': true,
        'isAllowChangeDeadlineForSelf': false,
        'isAllowChangeDeadlineForAssistant': true,

        'isAllowChangeWeightForLeader': true,
        'isAllowChangeWeightForSelf': false,
        'isAllowChangeWeightForAssistant': true,

        'isAllowChangeGoalTextForLeader': true,
        'isAllowChangeGoalTextForSelf': true,
        'isAllowChangeGoalTextForAssistant': true,

        'hiddenBaseAndPerformanceAmount': false,
        'notAllowGoalChangesInFirstStateForSelf': false,
        'firstStateDisabled': [{value: false, disabled: true}],

        'isTextBasedGoalsEnabled': true,
        'textBasedGoalsBlockNameHu': [''],
        'textBasedGoalsBlockNameEn': [''],
        'sliderMin': [0],
        'sliderMax': [0, 
          //CustomValidators.conditionalRequiredValidator((group) => group.parent.get('isGoalsEnabled')?.value != null && group.parent.get('isGoalsEnabled')?.value === true),    
        ],
        'sliderStep': [0,
          //CustomValidators.conditionalRequiredValidator((group) => group.parent.get('isGoalsEnabled')?.value != null && group.parent.get('isGoalsEnabled')?.value === true),
        ],
        'textBasedGoalsDefaultWeight': [null],
        
        'isNumericGoalsEnabled': true,
        'numericGoalsBlockNameHu': [''],
        'numericGoalsBlockNameEn': [''],
        'numericGoalsDefaultWeight': [null],
        'numericGoalMaxResult': [null],

        'isGroupGoalsEnabled': true,
        'isAllowChange': true
      })
    }, {
      validators: [
        CustomValidators.atLeastOneFieldTrueValidatorWithCondition(['isPositionBasedAssessment', 'isGoalsEnabled', 'isSpecialQuestionSetBasedAssessment'], (form) => form.get('reviewType')?.value === ReviewTypeEnum.TER),
        CustomValidators.customMultipleFieldCondition(['reviewPeriodGoalSettings.sliderMax', 'reviewPeriodGoalSettings.sliderStep'], (control) => control && !control.get('isGoalsEnabled')?.value ||  (!!control.get('reviewPeriodGoalSettings.sliderMax')?.value && !!control.get('reviewPeriodGoalSettings.sliderStep')?.value && control.get('reviewPeriodGoalSettings.sliderMax')?.value % control.get('reviewPeriodGoalSettings.sliderStep')?.value === 0), 'goalMaxValueShouldBeDivisibleByStep'),
      ] 
    });
  }

  createQuestionSettingsGroup(settings: ReviewPeriodQuestionSettings): FormGroup
  {
    return this.fb.group({
      'id': [settings.id ?? 0],
      'reviewPeriodId': settings.reviewPeriodId,
      'questionId': settings?.questionId,
      'questionName': [settings?.questionName ?? ''],
      'isSelfAssessmentAllowed': [settings?.isSelfAssessmentAllowed ?? false],
      'isLeaderAssessmentAllowed': [settings?.isLeaderAssessmentAllowed ?? false],
      'isPeerAssessmentAllowed': [settings?.isPeerAssessmentAllowed ?? false],
      'isSpecialQuestion': [settings?.isSpecialQuestion ?? false]
    });
  }

  public get isQuestionSetSelected(): boolean {
    return this.selectedQuestionSets.length > 0;
  }

  public removeHtml(html: string) {
    if (!html) return '';
      return html.replace(/<[^>]*>/g, '');
  }
  
  public addItem() {        
    this.addMode = false;
  }

  public cancelAddItem() {
    this.addMode = false;
  }

  public changeIsPositionBasedAssessment($event: any) {
    if ($event.checked) {
      this.loadDates('panoramaAuraStartDate', 'panoramaAuraEndDate');
    }

    this.updateModules($event);
  }

  public changeIsAura($event: any) {
    if ($event.checked) {
      this.loadDates('panoramaAuraStartDate', 'panoramaAuraEndDate');
    }

    this.updateModules($event);
  }

  public changeIsGoals($event: any) {
    if (!$event.checked) 
    {
      this.editForm.get('reviewPeriodGoalSettings').reset();
      return;
    }

    this.editForm.get('reviewPeriodGoalSettings').setValue({
      id: 0,
      reviewPeriodId: 0,

      // Settings permissions
      isAllowSettingsForLeader: true,
      isAllowSettingsForSelf: false,
      isAllowSettingsForAssistant: true,

      // Deadline permissions
      isAllowChangeDeadlineForLeader: true,
      isAllowChangeDeadlineForSelf: false,
      isAllowChangeDeadlineForAssistant: true,

      // Custom goals and groups permissions
      isAllowChangeWeightForLeader: true,
      isAllowChangeWeightForSelf: false,
      isAllowChangeWeightForAssistant: true,

      // Goal text permissions
      isAllowChangeGoalTextForLeader: true,
      isAllowChangeGoalTextForSelf: true,
      isAllowChangeGoalTextForAssistant: true,

      // General settings
      hiddenBaseAndPerformanceAmount: false,
      notAllowGoalChangesInFirstStateForSelf: false,
      firstStateDisabled: false,
      
      // Text based goals settings
      isTextBasedGoalsEnabled: true,
      textBasedGoalsBlockNameHu: 'Szöveges egyéni célok | értékelések',
      textBasedGoalsBlockNameEn: 'Text-based individual goals | evaluations',
      sliderMin: 0,
      sliderMax: 200,
      sliderStep: 10,
      textBasedGoalsDefaultWeight: null,

      // Numeric goals settings
      isNumericGoalsEnabled: true,
      numericGoalsBlockNameHu: 'Számszerű egyéni célok | OKR | értékelések',
      numericGoalsBlockNameEn: 'Quantitative individual goals | OKR | evaluations',
      numericGoalsDefaultWeight: null,
      numericGoalMaxResult: 100,

      // Group goals
      isGroupGoalsEnabled: true,
      
      isAllowChange: true
    });

    this.loadDates('goalsStartDate', 'goalsEndDate');
  }

  public get isTextBasedGoalsEnabled(): boolean {
    return !!this.editForm?.get('reviewPeriodGoalSettings.isTextBasedGoalsEnabled')?.value;
  }

  public get isNumericGoalsEnabled(): boolean {
    return !!this.editForm?.get('reviewPeriodGoalSettings.isNumericGoalsEnabled')?.value;
  }

  public loadDates(dateFromField: string, dateToField: string){
    this.editForm.get(dateFromField).setValue(this.editForm.get('startDate').value);
    this.editForm.get(dateToField).setValue(this.editForm.get('endDate').value);
  }

  public getFieldErrorText(formControlKey: string): string {
    const control = this.editForm.get(formControlKey);

    if (control && control.errors && Object.keys(control.errors).length > 0) {
      let error = Object.keys(control.errors)[0];
      return this.msgService.getErrorText(error);
    }
    return '';
  }

  public onDebugButton() {
    // iterate through the form controls and log their errors
    Object.keys(this.editForm.controls).forEach(key => {
      const control = this.editForm.get(key);
      if (control.errors) {
        console.log(`Control: ${key}, Errors:`, control.errors);
      }
    }
    );
  }
  
}
